import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Badge } from '@app/components/ui/badge'
import { cn } from '@app/lib/cn'
import {
  currencySymbol,
  formatAmount,
  formatCompactAmount,
  formatPairs,
  type DashboardPurchasingCurrency,
  type DashboardPurchasingSummary,
  type DashboardReceivableCurrency,
  type DashboardReceivablesSummary,
  type DashboardSummary,
} from './api'

/**
 * Six counters: orders, remaining pairs, production, stock, receivables and
 * open purchasing exposure — the last two only for the roles that may see them,
 * so the grid holds six, five or four. This screen is read from two meters away
 * on the shop floor in the morning: the number is large and mono (tabular), the
 * label small, the description a single line. Colour only kicks in when it
 * truly signals status (badge).
 *
 * The two money tiles print their figure PER CURRENCY underneath the headline
 * and never add them up — Korgun does not convert between TL, USD and EUR, so
 * a single "total" would be fiction (see `worker/api/cari.ts`). The headline is
 * therefore the leading currency's figure, in compact form, with the exact
 * amounts on the lines below.
 */

const tileLinkClass =
  'mt-2 inline-flex items-center gap-1 self-start text-micro font-medium text-kordovan underline-offset-2 hover:underline'

const inlineLinkClass = 'font-medium text-kordovan underline-offset-2 hover:underline'

/*
 * Every tile ends in the list it was counted from.
 *
 * The four money/stock tiles already did; the three operational ones were dead
 * numbers — the whole point of a morning screen is that the number you don't
 * like is one click from the rows behind it.
 *
 * The pre-filter travels as a search param whose vocabulary is the API's own
 * `OrderFilter` ('open' / 'overdue' — see `worker/api/orders.ts`), so the list
 * screen can read it straight into its filter state. NOTE FOR THE MERGE: the
 * orders list screen keeps its filters in React state today, so `?status=` is
 * inert until the sibling change that reads it lands. If that change picks a
 * different param name, this is the one place to change.
 */
// type: 'S' is load-bearing: the dashboard's order numbers count SALES orders
// only, while the list's default type is 'sales' (S + ST/numune). Without the
// explicit type the "36 open" tile would open 402 rows (36 S + 366 ST).
const ORDERS_OPEN_SEARCH = { status: 'open', type: 'S' } as const
const ORDERS_OVERDUE_SEARCH = { status: 'overdue', type: 'S' } as const

interface StatTileProps {
  label: string
  value: string
  unit?: string
  /** Single-line description under the number. */
  description: ReactNode
  /** Status badge in the top right. */
  badge?: ReactNode
  /** Per-currency lines between the number and the description. */
  lines?: ReactNode
  /** Small "→" link into the tile's own module. */
  link?: ReactNode
  /** Grid placement only — see `tileSpanClass` below. */
  className?: string
}

function StatTile({
  label,
  value,
  unit,
  description,
  badge,
  lines,
  link,
  className,
}: StatTileProps) {
  return (
    <article
      className={cn(
        'flex min-w-0 flex-col justify-between rounded-sm border border-hairline bg-surface px-3.5 py-3',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="eyebrow">{label}</h3>
        {badge}
      </div>

      <p className="mt-2 flex items-baseline gap-1.5">
        <span className="font-mono tnum text-[2.25rem] font-semibold leading-none tracking-tight text-ink lg:text-[2.75rem]">
          {value}
        </span>
        {unit ? <span className="text-micro text-ink-muted">{unit}</span> : null}
      </p>

      {lines}

      <p className="mt-2 text-micro leading-snug text-ink-muted">{description}</p>

      {link}
    </article>
  )
}

/** Exact per-currency amounts, TL first — the order the API already returns. */
function CurrencyLines({
  items,
  empty,
}: {
  items: readonly { currency: string; amount: number; note?: string }[]
  empty: string
}) {
  if (items.length === 0) {
    return <p className="mt-1.5 text-micro text-ink-muted">{empty}</p>
  }

  return (
    <ul className="mt-1.5 flex min-w-0 flex-col gap-0.5">
      {items.map((item) => (
        <li
          key={item.currency}
          className="flex items-baseline justify-between gap-2 font-mono text-micro tnum"
        >
          <span className="truncate text-ink">{formatAmount(item.amount, item.currency)}</span>
          {item.note ? (
            <span className="shrink-0 text-ink-muted">{item.note}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function receivableLines(currencies: readonly DashboardReceivableCurrency[]) {
  return currencies.map((c) => ({
    currency: c.currency,
    amount: c.amount,
    note: `${formatPairs(c.accountCount)} cari`,
  }))
}

function purchasingLines(currencies: readonly DashboardPurchasingCurrency[]) {
  return currencies.map((c) => ({ currency: c.currency, amount: c.amount }))
}

/*
 * Three tile counts, three grids that all end flush — an empty half-row would
 * read as a tile that failed to load.
 *
 *   6  two per row, three per row from xl: 2×3 → 3×2, both full.
 *   5  the same, with the odd tile out taking the leftover columns: the money
 *      tile it always is carries a currency list, so the width is the one it
 *      would have asked for anyway.
 *   4  two per row, one row of four from xl — three columns would strand one.
 *
 * Both helpers are exported because the loading skeleton draws the same grid
 * (features/dashboard/states.tsx); the placeholder must not reflow into a
 * different shape the moment the payload lands.
 */
export function statGridClass(tileCount: number): string {
  return cn('grid grid-cols-2 gap-3', tileCount === 4 ? 'xl:grid-cols-4' : 'xl:grid-cols-3')
}

/**
 * What the LAST tile wears when the count is odd — two columns, which is the
 * whole width at `grid-cols-2` and the two left over at `xl:grid-cols-3`.
 * Nothing on an even count, where every row already fills.
 */
export function tileSpanClass(tileCount: number): string | undefined {
  return tileCount % 2 === 1 ? 'col-span-2' : undefined
}

export function StatTiles({ summary }: { summary: DashboardSummary }) {
  const { orders, production, criticalStock, receivables, purchasing } = summary
  const measuringCritical = criticalStock.definedCardCount > 0

  /*
   * The PAYLOAD decides which money tiles exist, not the role: the Worker never
   * computes a block the account may not have and sends null in its place
   * (worker/api/dashboard.ts), so there is nothing here to hide — the check is
   * simply "did the data come". The two are asked separately because they are
   * granted separately: a `purchasing` account gets the second and not the
   * first.
   */
  const tileCount = 4 + (receivables !== null ? 1 : 0) + (purchasing !== null ? 1 : 0)
  // At most one money tile is drawn on an odd grid, so both may carry the span.
  const span = tileSpanClass(tileCount)

  return (
    <div className={statGridClass(tileCount)}>
      <StatTile
        label="Açık sipariş"
        value={formatPairs(orders.openCount)}
        unit="sipariş"
        badge={
          orders.overdueCount > 0 ? (
            <Badge tone="warn">{formatPairs(orders.overdueCount)} gecikti</Badge>
          ) : (
            <Badge tone="ok">Termin temiz</Badge>
          )
        }
        description={
          orders.upcomingCount > 0
            ? `${formatPairs(orders.upcomingCount)} siparişin termini 14 gün içinde.`
            : 'Önümüzdeki 14 günde termini dolan sipariş yok.'
        }
        link={
          <Link
            to="/siparisler"
            search={ORDERS_OPEN_SEARCH}
            className={tileLinkClass}
            title="Açık siparişleri listele"
          >
            Açık siparişler <span aria-hidden>→</span>
          </Link>
        }
      />

      {/*
        The remaining-pairs tile leads wherever its own sentence points: at the
        overdue rows when there are overdue pairs, at the open book otherwise.
        A link labelled "gecikenler" that opens everything would be a lie.
      */}
      <StatTile
        label="Kalan çift"
        value={formatPairs(orders.remainingPairs)}
        unit="çift"
        description={
          orders.overduePairs > 0
            ? `${formatPairs(orders.overduePairs)} çift gecikmiş siparişlerde bekliyor.`
            : 'Tamamı termini gelmemiş siparişlerde.'
        }
        link={
          orders.overduePairs > 0 ? (
            <Link
              to="/siparisler"
              search={ORDERS_OVERDUE_SEARCH}
              className={tileLinkClass}
              title="Termini geçmiş siparişleri listele"
            >
              Geciken siparişler <span aria-hidden>→</span>
            </Link>
          ) : (
            <Link
              to="/siparisler"
              search={ORDERS_OPEN_SEARCH}
              className={tileLinkClass}
              title="Sevk bekleyen siparişleri listele"
            >
              Sevk bekleyenler <span aria-hidden>→</span>
            </Link>
          )
        }
      />

      <StatTile
        label="Üretimde"
        value={formatPairs(production.totalPairs)}
        unit="çift"
        badge={<Badge tone="info">{formatPairs(production.inProgressEmirCount)} emir</Badge>}
        description={`${formatPairs(production.waitingPairs)} çift sırada, ${formatPairs(production.inProgressPairs)} çift tezgâhta.`}
        link={
          <Link to="/uretim" className={tileLinkClass} title="Üretim hattını aç">
            Üretim hattı <span aria-hidden>→</span>
          </Link>
        }
      />

      {/*
        Korgun'da `StokKart.KritikSeviye` 2.068 kartın tamamında 0 — kritik seviye
        hiç tanımlanmamış. Sıfır göstermek "her şey yolunda" yalanı olurdu, o
        yüzden ölçüt yoksa kart gerçekten var olan uyarıya döner: toplam mevcudu
        eksiye düşmüş kart sayısı. Etiket de neyi saydığını söyler.
      */}
      {measuringCritical ? (
        <StatTile
          label="Kritik stok"
          value={formatPairs(criticalStock.count)}
          unit="kart"
          badge={
            criticalStock.count > 0 ? (
              <Badge tone="warn">Seviye altında</Badge>
            ) : (
              <Badge tone="ok">Seviye üstünde</Badge>
            )
          }
          description={`${formatPairs(criticalStock.definedCardCount)} kartta kritik seviye tanımlı.`}
        />
      ) : (
        <StatTile
          label="Eksi bakiyeli stok"
          value={formatPairs(criticalStock.negativeCardCount)}
          unit="kart"
          badge={<Badge tone="warn">Sayım gerekli</Badge>}
          description={
            <>
              Kritik seviye hiçbir kartta tanımlı olmadığı için kritik stok
              ölçülemiyor.{' '}
              <Link to="/stok" className="text-kordovan underline-offset-2 hover:underline">
                Stok kartlarını aç
              </Link>
              .
            </>
          }
        />
      )}

      {receivables !== null ? <ReceivablesTile receivables={receivables} className={span} /> : null}
      {purchasing !== null ? <PurchasingTile purchasing={purchasing} className={span} /> : null}
    </div>
  )
}

/*
 * The money tiles, one component each and never rendered without the data the
 * API actually sent. Separate components rather than one `FinanceTiles` pair,
 * because the two blocks are granted separately — see `GUARDED_AREAS` in
 * worker/api/auth.contract.ts — and each is 40 lines of JSX that `StatTiles`
 * does not need to carry inline.
 */

function ReceivablesTile({
  receivables,
  className,
}: {
  receivables: DashboardReceivablesSummary
  className?: string
}) {
  // Headline figure: the leading currency's total.
  const leadReceivable = receivables.currencies.find(
    (c) => c.currency === receivables.leadCurrency,
  )

  /*
   * Receivables come from the cari LEDGER — Korgun's CariBakiye table is a dead
   * cache (350 of 524 stored balances are stale). The headline is the leading
   * currency's total; every currency keeps its own line and they are never
   * added together.
   */
  return (
    <StatTile
      className={className}
      label="Müşteri alacağı"
      value={leadReceivable ? formatCompactAmount(leadReceivable.amount) : '0'}
      unit={currencySymbol(receivables.leadCurrency) || undefined}
      badge={
        receivables.accountCount > 0 ? (
          <Badge tone="info">{formatPairs(receivables.accountCount)} cari</Badge>
        ) : (
          <Badge tone="ok">Alacak yok</Badge>
        )
      }
      lines={
        <CurrencyLines
          items={receivableLines(receivables.currencies)}
          empty="Açık müşteri alacağı yok."
        />
      }
      description={
        receivables.largest ? (
          <>
            En büyüğü{' '}
            <Link
              to="/cari/$ckod"
              params={{ ckod: receivables.largest.ckod }}
              title={`${receivables.largest.name} cari kartını aç`}
              className={inlineLinkClass}
            >
              {receivables.largest.name}
            </Link>{' '}
            · {formatAmount(receivables.largest.amount, receivables.largest.currency)}
          </>
        ) : (
          'Cari defterinde açık müşteri alacağı görünmüyor.'
        )
      }
      link={
        <Link to="/cari" className={tileLinkClass} title="Cari hesapları aç">
          Cari hesaplar <span aria-hidden>→</span>
        </Link>
      }
    />
  )
}

function PurchasingTile({
  purchasing,
  className,
}: {
  purchasing: DashboardPurchasingSummary
  className?: string
}) {
  /*
   * Open purchasing exposure: only orders that are still open (Durum = '') AND
   * still owing. The 97 manually closed orders that keep a remainder are
   * deliberately out — somebody already decided those will not arrive.
   */
  return (
    <StatTile
      className={className}
      label="Açık satınalma"
      value={formatPairs(purchasing.kalanQty)}
      unit="kalan"
      badge={
        purchasing.overdueCount > 0 ? (
          <Badge tone="warn">{formatPairs(purchasing.overdueCount)} gecikti</Badge>
        ) : (
          <Badge tone="info">{formatPairs(purchasing.openCount)} sipariş</Badge>
        )
      }
      lines={
        <CurrencyLines
          items={purchasingLines(purchasing.currencies)}
          empty="Açık siparişlerde tutar girilmemiş."
        />
      }
      description={
        purchasing.topSupplier ? (
          <>
            {formatPairs(purchasing.openCount)} açık siparişte{' '}
            {formatPairs(purchasing.supplierCount)} tedarikçi · en çok{' '}
            <span className="font-medium text-ink">{purchasing.topSupplier.name}</span> (
            {formatPairs(purchasing.topSupplier.kalanQty)})
          </>
        ) : (
          'Bekleyen satınalma siparişi yok.'
        )
      }
      link={
        <Link to="/satinalma" className={tileLinkClass} title="Satınalma siparişlerini aç">
          Satınalma <span aria-hidden>→</span>
        </Link>
      }
    />
  )
}
